import React, { useState, useEffect, useRef, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GoArrowLeft } from "react-icons/go";
import { AnimatePresence, motion } from "framer-motion";
import { MdCallReceived } from "react-icons/md";
import { AssistantAllButtonTypes, CodeFile } from "utils/interfaces";
import { fetchVideoData } from "services/filesRequests";
import Video from "components/Editor/utils/Video";
import Player from "@vimeo/player";
import AssistantPopup from "components/Editor/assistant/AssistantPopup";
import { usePlatform } from "components/utils/PlatformContext";
import {
  EditorContext,
  PythonJSEnvironmentContext,
} from "components/utils/Contexts";
import useElementSize from "hooks/ElementSize";

interface CodeEditorHeaderProps {
  name: string;
  video: string;
  videoExpanded: boolean;
  setVideoExpanded: (bool: boolean) => void;
  hideVideo: boolean;
  showAssistant: boolean;
  codeFiles: CodeFile[];
}

const CodeEditorHeader: React.FC<CodeEditorHeaderProps> = ({
  name,
  video,
  videoExpanded,
  setVideoExpanded,
  hideVideo,
  showAssistant,
  codeFiles,
}) => {
  // States definition
  const [videoThumbnail, setVideoThumbnail] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const [assistantPopupState, setAssistantPopupState] = useState<
    "closed" | "popup" | "popupNoCode" | "expanded" | "fullscreen"
  >("closed");
  const [assistantPopupClickedButton, setAssistantPopupClickedButton] =
    useState<AssistantAllButtonTypes>(undefined);

  // Get the platform context
  const { logo } = usePlatform();
  // Get the editor parameters
  const { AIAssistant } = useContext(EditorContext);
  // Define a navigate hook
  const navigate = useNavigate();
  // Get the parameters from the url
  const { moduleOrProject } = useParams();
  // Get a state if we are receiving a state variable from another url
  const { state } = useLocation();

  // Define an image reference
  const imgRef = useRef<HTMLImageElement>(null);
  // Define a reference for the video iframe
  const videoIframeRef = useRef<HTMLIFrameElement>(null);
  // Define a reference for the player in order to avoid memory leaks
  const playerRef = useRef<any>(null);

  // Get context parameters
  const { activeItem, setActiveItem, pythonJSHeaderOffset } = useContext(
    PythonJSEnvironmentContext
  );

  // Control the size of the video when resizing
  const [containerSize, imgContainerRef] = useElementSize<HTMLDivElement>();

  useEffect(() => {
    if (videoExpanded) {
      const timer = setTimeout(() => {
        setShowVideo(true);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setShowVideo(false);
    }
  }, [videoExpanded]);

  /**
   * Define the player reference once
   */
  useEffect(() => {
    if (videoIframeRef.current && !playerRef.current) {
      playerRef.current = new Player(videoIframeRef.current);
    }

    // Clean up when unmounting
    return () => {
      if (playerRef.current) {
        playerRef.current.unload(); // or destroy(), depending on the API
        playerRef.current = null;
      }
    };
  }, []);

  /**
   * Hook used for fetching video title and thumbnail
   */
  useEffect(() => {
    if (activeItem === "editor" && video) {
      fetchVideoData(video).then((imageUrl: string) => {
        setVideoThumbnail(imageUrl);
      });
    }
  }, [video]);

  /**
   * Hook to stop the video if the AI assistant is fullscreen
   */
  useEffect(() => {
    if (assistantPopupState === "fullscreen") pauseVideo();
  }, [assistantPopupState]);

  // Return to home url
  const goToDashboard = () => {
    if (state) {
      navigate("/home", {
        state: {
          // State for MainView.tsx
          activeItem: "lessons",
          // State for Lessons.tsx
          activeLesson: moduleOrProject,
          navRegister: state ? state.navRegister : [],
        },
      });
    } else {
      navigate("/home");
    }
  };

  /**
   * Function used to pause video
   */
  const pauseVideo = () => {
    if (playerRef.current) {
      const player = playerRef.current;
      player.pause();
    }
  };

  /**
   * Function that defines the contextual behaviour of the back button
   */
  const handleClickBack = () => {
    if (activeItem === "assistant") setActiveItem("editor");
    else if (activeItem === "editor") goToDashboard();
  };

  const handleThumbnailClick = () => {
    // Start pausing the video
    pauseVideo();

    console.log("expanding video...", !videoExpanded);
    setVideoExpanded(!videoExpanded);
  };

  return (
    <>
      <div
        className="w-full pt-8 px-10 relative flex items-center z-20"
        style={{ height: pythonJSHeaderOffset }}
      >
        {/* Logo and back button */}
        <div className="left-10 flex items-center gap-8">
          <div className="w-[124px] h-16 flex items-center justify-center">
            <img
              className="w-auto h-auto max-w-full max-h-full object-contain cursor-pointer"
              src={logo}
              onClick={goToDashboard}
              alt="Platform logo"
              role="main"
            />
          </div>
          <div className="w-px self-stretch bg-neutral-200" />
          <div className="flex-col justify-start items-center gap-2 inline-flex">
            <button
              className="w-12 h-12 p-3 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-full justify-center items-center inline-flex cursor-pointer z-10"
              onClick={handleClickBack}
              title="Go back"
            >
              <GoArrowLeft className="w-6 h-6 fill-secondary-600" />
            </button>
            <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide select-none">
              Back
            </div>
          </div>
        </div>

        {/* Title */}
        <h1 className="flex-grow flex flex-col justify-center items-center">
          <div className="text-center text-neutral-600 text-[23px] font-bold font-sans leading-7">
            {activeItem === "assistant" ? "Feedback" : name}
          </div>
          {activeItem === "assistant" && (
            <div className="text-center text-neutral-500 text-base font-normal font-sans leading-[19px]">
              {`Tutoring with ${AIAssistant.name}`}
            </div>
          )}
        </h1>

        {/* Assistant and video buttons */}
        <div className="right-10 flex items-center mr-3 z-10">
          {/* Conditional rendering for Assistant */}
          {showAssistant && (
            <AssistantPopup
              AIAssistant={AIAssistant}
              state={assistantPopupState}
              setState={setAssistantPopupState}
              clickedButton={assistantPopupClickedButton}
              setClickedButton={setAssistantPopupClickedButton}
              codeFiles={codeFiles}
              goToDashboard={goToDashboard}
              language={null}
            />
          )}

          {/* Video placeholder */}
          {video !== "" && (
            <>
              <div className="w-6" />
              <div className="relative h-[111px] w-[180px]">
                {videoExpanded ? (
                  <AnimatePresence>
                    <motion.div
                      key="video-expanded"
                      className="group cursor-pointer absolute overflow-hidden h-[111px] w-[180px] z-[5] rounded-lg"
                      initial={{
                        opacity: 0,
                        backdropFilter: "blur(0px)",
                        background: "rgb(0 0 0 / 10%)",
                        right: 100,
                        top: 200,
                      }}
                      animate={{
                        opacity: 1,
                        right: 0,
                        top: 0,
                        backdropFilter: "blur(2px)",
                      }}
                      exit={{
                        opacity: 0,
                        backdropFilter: "blur(0px)",
                        right: 100,
                        top: 200,
                      }}
                      onClick={handleThumbnailClick}
                    >
                      <motion.div className="h-[22px] px-3 py-1 bg-neutral-50 rounded shadow backdrop-blur-[32px] justify-center items-center gap-3 inline-flex right-2 top-2 absolute">
                        <div className="text-secondary-700 text-xs font-semibold font-poppins uppercase leading-[14px] tracking-wide select-none">
                          Show Code
                        </div>
                      </motion.div>
                      <motion.div className="h-10 w-10 p-1 bg-neutral-400 bg-opacity-50 rounded-tr rounded-bl-lg shadow backdrop-blur-xl justify-center gap-2 inline-flex left-0 bottom-0 absolute items-center group-hover:size-12 ease-in-out duration-200">
                        <MdCallReceived className="h-full w-full fill-white" />
                      </motion.div>
                    </motion.div>
                  </AnimatePresence>
                ) : (
                  <AnimatePresence initial={false}>
                    <motion.div
                      key="code-expanded"
                      className="group cursor-pointer absolute overflow-hidden h-[111px] w-[180px] z-[5] rounded-lg"
                      onClick={handleThumbnailClick}
                      initial={{
                        opacity: 0,
                        right: 100,
                        top: 200,
                      }}
                      animate={{
                        opacity: 1,
                        right: 0,
                        top: 0,
                      }}
                      exit={{
                        opacity: 0,
                        right: 100,
                        top: 200,
                      }}
                    >
                      <motion.div className="h-[22px] px-3 py-1 bg-neutral-50 rounded shadow backdrop-blur-[32px] justify-center items-center gap-3 inline-flex right-2 top-2 absolute">
                        <div className="text-secondary-700 text-xs font-semibold font-poppins uppercase leading-[14px] tracking-wide select-none">
                          Play Video
                        </div>
                      </motion.div>
                      <motion.div className="h-10 w-10 p-1 bg-gradient-to-tr white rounded-tr rounded-bl-lg shadow backdrop-blur-xl justify-center gap-2 inline-flex left-0 bottom-0 absolute items-center group-hover:size-12 ease-in-out duration-200">
                        <MdCallReceived className="h-full w-full fill-white" />
                      </motion.div>
                    </motion.div>
                  </AnimatePresence>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile thumbnail and video */}
      <motion.div
        ref={imgContainerRef}
        className={`absolute w-full h-screen items-center max-h-[calc(100vh-203px)] right-12 ${
          hideVideo ? "hidden" : "block"
        }`}
        initial={{
          top: 22,
          width: 155,
        }}
        animate={{
          top: videoExpanded ? 155 : 22,
          width: videoExpanded ? "calc(100% - 96px)" : 180,
          height: videoExpanded ? undefined : 111,
          right: videoExpanded ? undefined : 52,
          transition: { ease: "linear" },
        }}
      >
        <div
          className={`h-full w-full rounded-lg mx-auto relative inline-flex justify-center items-center ${
            videoExpanded ? "z-1" : ""
          }`}
        >
          <motion.div
            ref={imgRef}
            className={`h-full w-fit ${showVideo ? "hidden" : "block"}`}
          >
            {videoThumbnail ? (
              <img
                className={`h-full rounded-lg mx-auto`}
                src={videoThumbnail}
                alt="Video Thumbnail"
              />
            ) : (
              video && (
                <div className="h-[111px] w-[180px] rounded-lg mx-auto bg-neutral-100" />
              )
            )}
          </motion.div>
          <Video
            iframeRef={videoIframeRef}
            width={containerSize.width}
            className={`rounded-xl mx-auto overflow-hidden ${
              showVideo ? "block" : "hidden"
            }`}
            isOpen={true}
            videoUrl={video}
            maxHeight={containerSize.height}
          />
        </div>
      </motion.div>
    </>
  );
};

export default CodeEditorHeader;
